import { jsPDF } from "jspdf";

import { MatrizEvidencia } from "../../shared/model/iso42001/matriz-riesgo.model";
import { Verificacion, fechaPdf, tamanoLegible } from "./expediente-formato";

/** Un color RGB para jsPDF. */
export type ColorPdf = [number, number, number];

/** La portada: lo que cambia entre el expediente del activo y el del riesgo. */
export interface PortadaPdf {
  titulo: string;
  rubro: string;
  folio: string;
  /** Cuánto baja el cursor después del folio. */
  saltoFolio: number;
  nombre: string;
  /** La línea con el riesgo de partida y en el que queda. */
  riesgo: string;
}

/** El rojo del formato: membrete, franja de cada sección y folio. */
const ROJO: ColorPdf = [183, 28, 28];
/** Márgenes de la hoja, en milímetros. */
const MARGEN_IZQ = 18;
const MARGEN_DER = 18;
const MARGEN_SUP = 22;
const MARGEN_INF = 18;
/** Ancho de la columna de etiquetas en las filas etiqueta / valor. */
const ANCHO_ETIQUETA = 54;

/**
 * El expediente imprimible: documento real por secciones (texto, no captura
 * de pantalla), con el mismo armado que el PDF de denuncias. Lleva la hoja A4,
 * el cursor vertical y las piezas con que cada expediente arma sus secciones.
 */
export class PdfExpediente {
  readonly pdf = new jsPDF("p", "mm", "a4");
  readonly pageW: number = this.pdf.internal.pageSize.getWidth();
  readonly pageH: number = this.pdf.internal.pageSize.getHeight();
  readonly contentW = this.pageW - MARGEN_IZQ - MARGEN_DER;
  readonly margenIzq = MARGEN_IZQ;
  /** Dónde va el siguiente renglón. */
  y = MARGEN_SUP;

  /** @param fondoTitulo el fondo de la banda de cada sección. */
  constructor(private readonly fondoTitulo: ColorPdf) {}

  tinta(): void {
    this.pdf.setTextColor(33, 37, 41);
  }

  tenue(): void {
    this.pdf.setTextColor(110, 116, 128);
  }

  /** Salta de página si lo que sigue ya no cabe. */
  asegura(alto: number): void {
    if (this.y + alto > this.pageH - MARGEN_INF) {
      this.pdf.addPage();
      this.y = MARGEN_SUP;
    }
  }

  /** La banda que abre cada sección. */
  titulo(txt: string): void {
    this.asegura(15);
    const alto = 8.5;
    const [r, g, b] = this.fondoTitulo;
    this.pdf.setFillColor(r, g, b);
    this.pdf.rect(MARGEN_IZQ, this.y, this.contentW, alto, "F");
    this.pdf.setFillColor(ROJO[0], ROJO[1], ROJO[2]);
    this.pdf.rect(MARGEN_IZQ, this.y, 1.8, alto, "F");
    this.pdf.setFont("helvetica", "bold");
    this.pdf.setFontSize(10.5);
    this.pdf.setTextColor(ROJO[0], ROJO[1], ROJO[2]);
    this.pdf.text((txt || "").toUpperCase(), MARGEN_IZQ + 5.5, this.y + 5.6);
    this.y += alto + 4.5;
  }

  parrafo(txt: string): void {
    this.pdf.setFont("helvetica", "normal");
    this.pdf.setFontSize(10);
    this.tinta();
    const lineas: string[] = this.pdf.splitTextToSize(txt?.trim() ? txt : "—", this.contentW - 4);
    for (const ln of lineas) {
      this.asegura(5.4);
      this.pdf.text(ln, MARGEN_IZQ + 2, this.y + 3.7);
      this.y += 5.3;
    }
    this.y += 2.5;
  }

  /** Una fila etiqueta / valor con su raya abajo. */
  fila(etiqueta: string, valor: string): void {
    this.pdf.setFont("helvetica", "normal");
    this.pdf.setFontSize(9.5);
    // La etiqueta también se envuelve: una larga se encimaba con su valor.
    const lineasEt: string[] = this.pdf.splitTextToSize(etiqueta?.trim() ? etiqueta : "—", ANCHO_ETIQUETA - 6);
    const lineasVal: string[] = this.pdf.splitTextToSize(valor?.trim() ? valor : "—", this.contentW - ANCHO_ETIQUETA - 4);
    const alto = Math.max(7, lineasVal.length * 5 + 2, lineasEt.length * 5 + 2);
    this.asegura(alto);
    this.tenue();
    this.pdf.setFont("helvetica", "bold");
    this.escribe(lineasEt, MARGEN_IZQ + 2);
    this.tinta();
    this.pdf.setFont("helvetica", "normal");
    this.escribe(lineasVal, MARGEN_IZQ + ANCHO_ETIQUETA);
    this.pdf.setDrawColor(235, 238, 242);
    this.pdf.setLineWidth(0.2);
    this.pdf.line(MARGEN_IZQ + 2, this.y + alto - 1, this.pageW - MARGEN_DER - 2, this.y + alto - 1);
    this.y += alto;
  }

  /** Un texto envuelto con sangría, de a 5 mm por renglón. */
  sangrado(texto: string, sangria: number, recorte: number): void {
    const lineas: string[] = this.pdf.splitTextToSize(texto, this.contentW - recorte);
    for (const ln of lineas) {
      this.asegura(5.2);
      this.pdf.text(ln, MARGEN_IZQ + sangria, this.y + 3.5);
      this.y += 5;
    }
  }

  /** Los renglones de una columna de la fila, desde el renglón actual. */
  private escribe(lineas: string[], x: number): void {
    let yy = this.y;
    for (const ln of lineas) {
      this.pdf.text(ln, x, yy + 4.2);
      yy += 5;
    }
  }

  /** La franja roja de arriba. */
  membrete(): void {
    this.pdf.setFillColor(ROJO[0], ROJO[1], ROJO[2]);
    this.pdf.rect(0, 0, this.pageW, 12, "F");
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.setFontSize(10);
    this.pdf.text("SOLSER · ISO/IEC 42001", MARGEN_IZQ, 7.8);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.setFontSize(8);
    this.pdf.text("EVIDENCIA DE TRAZABILIDAD", this.pageW - MARGEN_DER, 7.8, { align: "right" });
  }

  /** Título, rubro, folio, nombre y riesgo, centrados, y la raya que los separa del cuerpo. */
  portada(p: PortadaPdf): void {
    this.tinta();
    this.pdf.setFont("helvetica", "bold");
    this.pdf.setFontSize(19);
    this.centrado(p.titulo, this.y + 2);
    this.y += 9;
    this.tenue();
    this.pdf.setFont("helvetica", "bold");
    this.pdf.setFontSize(8);
    this.centrado(p.rubro, this.y);
    this.y += 5.5;
    this.pdf.setTextColor(ROJO[0], ROJO[1], ROJO[2]);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.setFontSize(15);
    this.centrado(p.folio, this.y);
    this.y += p.saltoFolio;
    this.tinta();
    this.pdf.setFont("helvetica", "normal");
    this.pdf.setFontSize(10.5);
    this.centrado(p.nombre, this.y);
    this.y += 5.5;
    this.tenue();
    this.pdf.setFontSize(9);
    this.centrado(p.riesgo, this.y);
    this.y += 8;
    this.pdf.setDrawColor(224, 228, 234);
    this.pdf.setLineWidth(0.4);
    this.pdf.line(MARGEN_IZQ, this.y, this.pageW - MARGEN_DER, this.y);
    this.y += 8;
  }

  private centrado(txt: string, y: number): void {
    this.pdf.text(txt, this.pageW / 2, y, { align: "center" });
  }

  /** El resumen en prosa y los cruces que deben cuadrar, con su semáforo. */
  resumen(texto: string, checks: Verificacion[]): void {
    this.titulo("Resumen");
    this.parrafo(texto);
    this.y += 2;
    if (checks.length) {
      this.titulo("Verificación de consistencia");
      checks.forEach((c) => this.fila(c.ok ? "Cumple" : "REVISAR", c.texto));
      this.y += 2;
    }
  }

  /** Una evidencia adjunta: su fecha, el archivo con su tamaño, quién la subió y lo que se agregue al final. */
  evidencia(e: MatrizEvidencia, cola: string): void {
    this.fila(fechaPdf(e.fechaCarga),
      `${e.nombreArchivo || "—"} (${tamanoLegible(e.tamanioBytes)})`
      + (e.usuarioNombre ? ` — subió: ${e.usuarioNombre}` : "")
      + cola);
  }

  /** El folio y la numeración al pie de cada página; después lo descarga con ese nombre. */
  terminar(consecutivo: string, archivo: string): void {
    const paginas = this.pdf.getNumberOfPages();
    const generado = fechaPdf(new Date(), true);
    for (let p = 1; p <= paginas; p++) {
      this.pdf.setPage(p);
      this.tenue();
      this.pdf.setFont("helvetica", "normal");
      this.pdf.setFontSize(7.5);
      this.pdf.text(`Expediente ${consecutivo} · generado el ${generado}`, MARGEN_IZQ, this.pageH - 8);
      this.pdf.text(`Página ${p} de ${paginas}`, this.pageW - MARGEN_DER, this.pageH - 8, { align: "right" });
    }
    this.pdf.save(archivo);
  }
}
